import { Action } from 'redux';
import { put, call } from '@redux-saga/core/effects';
import { CheckoutActions, CheckoutActionTypes } from '../checkout.types';
import { ACTION_CREATOR_NAME, analyticsHelper, errorToBasketErrorModel, hashEmailWithCryptoJS } from 'helpers';
import { ErrorAction } from '../../root.types';
import { api } from 'repositories/api';
import { authenticateCustomerSaga } from './loginProcess';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';
import { verifyEmailExistsSaga, VerifyEmailExistsResult } from './verifyEmailExists';

export interface RegisterProcessAction extends Action<CheckoutActionTypes.REGISTER_PROCESS> {
    path: string;
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
}

export interface RegisterProcessActionStart extends Action<CheckoutActionTypes.REGISTER_PROCESS_START> {
}

export interface RegisterProcessActionSuccess extends Action<CheckoutActionTypes.REGISTER_PROCESS_SUCCESS> {
}

export interface RegisterProcessActionError extends ErrorAction<CheckoutActionTypes.REGISTER_PROCESS_ERROR> {
}

export const registerProcess = (
    path: string,
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
): RegisterProcessAction => ({
    type: CheckoutActionTypes.REGISTER_PROCESS,
    path,
    firstName,
    lastName,
    email,
    password
});
registerProcess[ACTION_CREATOR_NAME] = 'registerProcess';

export type RegisterProcessFunc = (...args: Parameters<typeof registerProcess>) => void;

export function* registerProcessSaga({ path, firstName, lastName, email, password }: RegisterProcessAction): BasketSagaReturnType {
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.REGISTER_PROCESS_START
    });

    try {
        const emailCheck: VerifyEmailExistsResult = yield call(verifyEmailExistsSaga, email);

        if (!emailCheck.success) {
            yield put<CheckoutActions>({
                type: CheckoutActionTypes.CHECK_EMAIL_ERROR,
                error: errorToBasketErrorModel(emailCheck.error, 'registerProcessSage:verifyEmailExistsSaga')
            });

            yield put<CheckoutActions>({
                type: CheckoutActionTypes.REGISTER_PROCESS_ERROR,
                error: errorToBasketErrorModel(
                    emailCheck.error || new Error('Email check failed'),
                    'registerProcessSaga'
                )
            });

            sendRegisterFailAnalytics(path);

            return false;
        }

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.CHECK_EMAIL_SUCCESS,
            userExists: Boolean(emailCheck.userExists),
            email
        });

        if (emailCheck.userExists) {
            yield put<CheckoutActions>({
                type: CheckoutActionTypes.REGISTER_PROCESS_SUCCESS
            });

            return false;
        }

        const userData = yield api.custex.createCustomer(
            email.toLowerCase(),
            password,
            firstName ?? '',
            lastName ?? '');

        /** integration with third party services (e.g. exponeaService) */

        api.google.sendHashedEmail(email, 'Register');

        if (userData.guid) {
            yield callBasketSaga(authenticateCustomerSaga, email, password, path);

            yield put<CheckoutActions>({
                type: CheckoutActionTypes.REGISTER_PROCESS_SUCCESS
            });
        }

        analyticsHelper.sendToGoogleLoginMessage(
            path, 'user-register-success', { 'marketing-signup': 'no', hashedEmail: hashEmailWithCryptoJS(email) }
        );

        return true;
    } catch (error) {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.REGISTER_PROCESS_ERROR,
            error: errorToBasketErrorModel(error, 'registerProcessSaga')
        });

        sendRegisterFailAnalytics(path);

        return false;
    }

    function sendRegisterFailAnalytics(path: string): void {
        analyticsHelper.sendToGoogleLoginMessage(
            path,
            'user-register-fail',
            { 'validation-error': 'There was a problem creating your account. Please try again later or call 03444 81 81 81 to speak to a sofologist.' }
        );
    }
}